/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _Object$getPrototypeOf from "@babel/runtime-corejs3/core-js-stable/object/get-prototype-of";
import _Object$getOwnPropertyDescriptor from "@babel/runtime-corejs3/core-js-stable/object/get-own-property-descriptor";
import _setTimeout from "@babel/runtime-corejs3/core-js-stable/set-timeout";
import _forEachInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/for-each";
import _concatInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/concat";
import _sortInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/sort";
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _Set from "@babel/runtime-corejs3/core-js-stable/set";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/utils/input-renderer */
// TODO: USE OWN UNIQUE ID SYSTEM? APPLY TO ALL PROJECT.
import { objectUtils } from '@codistica/core';
import React, { createContext } from 'react';
import { default as uniqueId } from 'react-html-id';
import { InputPluginManager } from '../classes/input-plugin-manager.js';
import { InputValidatorPluginUtils } from '../classes/input-validator-plugin-utils.js';
var InputContext = createContext({
  formInstance: null
});
/**
 * @typedef inputRendererErrorMessagesType
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [mandatory=null] - Mandatory error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [match=null] - Match error message.
 */

/**
 * @typedef inputRendererPropsTypeType
 * @property {Object<string,*>} inputRenderFn - Input component render prop.
 * @property {string} name - Input name.
 * @property {string} value - Input value.
 * @property {(string|null)} [voidValue=''] - Value to consider input to be void.
 * @property {boolean} [booleanInput=null] - Boolean input flag.
 * @property {boolean} [mandatory=false] - Input mandatory flag.
 * @property {boolean} [keepMissingStatus=false] - Keep missing status after user interaction.
 * @property {boolean} [runFiltersBeforeValidators=false] - Filter input value before passing it to validators.
 * @property {(string|null)} [match=null] - Name of input that has to be matched to correctly validate.
 * @property {inputRendererErrorMessagesType} [errorMessages] - Validation error messages.
 * @property {*} [plugins=[]] - Input plugins.
 * @property {(null|function(*): string)} [stringifier=null] - Value stringifier.
 * @property {(null|function(string): *)} [parser=null] - Value parser.
 * @property {boolean} [deferValidation=true] - Defer input validation until there is an interaction.
 * @property {Function} [onValidationResult=null] - Callback for validationResult event.
 * @property {Function} [onKeyDown=null] - Callback for keyDown event.
 * @property {Function} [onChange=null] - Callback for change event.
 * @property {Function} [onBlur=null] - Callback for blur event.
 */

/**
 * @classdesc A beautiful input renderer.
 */

var InputRenderer = /*#__PURE__*/function (_React$Component) {
  _inherits(InputRenderer, _React$Component);

  var _super = _createSuper(InputRenderer);

  /**
   * @description Constructor.
   * @param {inputRendererPropsTypeType} [props] - Component props.
   */
  function InputRenderer(props) {
    var _context, _context2, _context3, _context4, _context5, _context6, _context7, _context8, _context9, _context10;

    var _this;

    _classCallCheck(this, InputRenderer);

    _this = _super.call(this, props);

    _defineProperty(_assertThisInitialized(_this), "nextUniqueId", void 0);

    _defineProperty(_assertThisInitialized(_this), "id", void 0);

    _defineProperty(_assertThisInitialized(_this), "hasInteracted", void 0);

    _defineProperty(_assertThisInitialized(_this), "isVoid", void 0);

    _defineProperty(_assertThisInitialized(_this), "isDeferred", void 0);

    _defineProperty(_assertThisInitialized(_this), "isStandby", void 0);

    _defineProperty(_assertThisInitialized(_this), "isMissing", void 0);

    _defineProperty(_assertThisInitialized(_this), "isMatched", void 0);

    _defineProperty(_assertThisInitialized(_this), "inputPluginManager", void 0);

    _defineProperty(_assertThisInitialized(_this), "validatorsOutput", void 0);

    _defineProperty(_assertThisInitialized(_this), "validationObject", void 0);

    _defineProperty(_assertThisInitialized(_this), "attachedPromises", void 0);

    _defineProperty(_assertThisInitialized(_this), "inputChangeTracker", void 0);

    uniqueId.enableUniqueIds(_assertThisInitialized(_this));
    _this.id = _this.nextUniqueId();
    _this.hasInteracted = false;
    _this.isVoid = null;
    _this.isDeferred = null;
    _this.isStandby = null;
    _this.isMissing = null;
    _this.isMatched = null;
    _this.inputPluginManager = new InputPluginManager();

    _this.inputPluginManager.loadPlugins(props.plugins);

    _this.validatorsOutput = {};
    _this.validationObject = {
      result: null,
      reports: {},
      messages: [],
      data: {}
    };
    _this.attachedPromises = new _Set();
    _this.inputChangeTracker = props.value;
    _this.state = {
      status: null,
      value: props.value,
      overrideStatus: false
    }; // BIND METHODS

    _assertThisInitialized(_this).setNewValue = _bindInstanceProperty(_context = _this.setNewValue).call(_context, _assertThisInitialized(_this));
    _assertThisInitialized(_this).validateInput = _bindInstanceProperty(_context2 = _this.validateInput).call(_context2, _assertThisInitialized(_this));
    _assertThisInitialized(_this).updateStatus = _bindInstanceProperty(_context3 = _this.updateStatus).call(_context3, _assertThisInitialized(_this));
    _assertThisInitialized(_this).highlight = _bindInstanceProperty(_context4 = _this.highlight).call(_context4, _assertThisInitialized(_this));
    _assertThisInitialized(_this).warn = _bindInstanceProperty(_context5 = _this.warn).call(_context5, _assertThisInitialized(_this));
    _assertThisInitialized(_this).clear = _bindInstanceProperty(_context6 = _this.clear).call(_context6, _assertThisInitialized(_this));
    _assertThisInitialized(_this).emulateChange = _bindInstanceProperty(_context7 = _this.emulateChange).call(_context7, _assertThisInitialized(_this));
    _assertThisInitialized(_this).onKeyDownHandler = _bindInstanceProperty(_context8 = _this.onKeyDownHandler).call(_context8, _assertThisInitialized(_this));
    _assertThisInitialized(_this).onChangeHandler = _bindInstanceProperty(_context9 = _this.onChangeHandler).call(_context9, _assertThisInitialized(_this));
    _assertThisInitialized(_this).onBlurHandler = _bindInstanceProperty(_context10 = _this.onBlurHandler).call(_context10, _assertThisInitialized(_this));
    return _this;
  }
  /**
   * @instance
   * @description React lifecycle.
   * @returns {void} Void.
   */


  _createClass(InputRenderer, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      // REGISTER INPUT
      this.context.formInstance && this.context.formInstance.registerInput(this); // VALIDATE INITIAL VALUE

      this.validateInput(); // LINK INPUTS FOR SUCCESSIVE VALIDATIONS

      if (this.props.match) {
        this.context.formInstance && this.context.formInstance.linkInputs(this.props.name, this.props.match.replace(/^!/, ''));
      }
    }
    /**
     * @instance
     * @description React lifecycle.
     * @returns {void} Void.
     */

  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      // UNLINK INPUT
      if (this.props.match) {
        this.context.formInstance && this.context.formInstance.unlinkInput(this.props.name);
      } // UNREGISTER INPUT


      this.context.formInstance && this.context.formInstance.unregisterInput(this);
    }
    /**
     * @instance
     * @description Callback for newValue event.
     * @param {string} value - New value.
     * @returns {void} Void.
     */

  }, {
    key: "setNewValue",
    value: function setNewValue(value) {
      var _this2 = this,
          _context11;

      // SET NEW VALUE
      this.setState({
        value: value
      }, _bindInstanceProperty(_context11 = function _context11() {
        _newArrowCheck(this, _this2);

        // VALIDATE NEW VALUE
        this.validateInput(); // REQUEST LINKED INPUTS VALIDATION

        this.context.formInstance && this.context.formInstance.validateLinkedInputs(this.props.name);
      }).call(_context11, this));
    }
    /**
     * @instance
     * @description Checks input based on its mandatory prop.
     * @returns {(boolean|null)} Result.
     */

  }, {
    key: "checkMandatory",
    value: function checkMandatory() {
      this.isVoid = this.state.value === this.props.voidValue;
      return this.props.mandatory && this.isVoid;
    }
    /**
     * @instance
     * @description Run input validation process.
     * @param {boolean} [useLastValidatorsOutput] - Use last validators output instead of running validators again.
     * @returns {void} Void.
     */

  }, {
    key: "validateInput",
    value: function validateInput(useLastValidatorsOutput) {
      var _this3 = this;

      this.isDeferred = this.props.deferValidation && !this.hasInteracted;
      this.isStandby = false;
      this.isMissing = this.checkMandatory();
      this.isMatched = this.context.formInstance && this.context.formInstance.checkMatch(this.props.name); // RESET VALIDATION OBJECT

      this.validationObject.result = null;
      this.validationObject.reports = {};
      this.validationObject.messages = [];
      this.validationObject.data = {};

      if (this.isMissing) {
        // RESULT
        this.validationObject.result = false; // MESSAGES

        if (!this.isDeferred && this.props.errorMessages.mandatory) {
          this.validationObject.messages = [InputValidatorPluginUtils.createMessageObject(this.props.errorMessages.mandatory)];
        }
      } else if (this.isMatched === false) {
        // RESULT
        this.validationObject.result = false; // MESSAGES

        if (!this.isDeferred && this.props.errorMessages.match) {
          this.validationObject.messages = [InputValidatorPluginUtils.createMessageObject(this.props.errorMessages.match)];
        }
      } else {
        (function () {
          var _this4 = this;

          if (!useLastValidatorsOutput) {
            // RUN VALIDATORS
            _this3.validatorsOutput = _this3.inputPluginManager.runValidators(_this3.props.runFiltersBeforeValidators ? _this3.inputPluginManager.runFilters(_this3.state.value) : _this3.state.value);
          }

          var activePromisesCount = 0;

          for (var validatorName in _this3.validatorsOutput) {
            var _context12, _context13, _context14, _context15, _context16;

            if (!Object.hasOwnProperty.call(_this3.validatorsOutput, validatorName)) {
              continue;
            }

            var validatorOutput = _this3.validatorsOutput[validatorName]; // RESULT

            if (_this3.validationObject.result === null) {
              _this3.validationObject.result = validatorOutput.result;
            } else if (_this3.validationObject.result === true && validatorOutput.result !== null) {
              _this3.validationObject.result = validatorOutput.result;
            } // REPORTS


            _this3.validationObject.reports[validatorName] = validatorOutput.report; // MESSAGES

            _this3.validationObject.messages = _sortInstanceProperty(_context12 = _concatInstanceProperty(_context13 = _this3.validationObject.messages).call(_context13, objectUtils.getValuesArray(validatorOutput.messages))).call(_context12, _bindInstanceProperty(_context14 = function _context14(msgA, msgB) {
              _newArrowCheck(this, _this4);

              return (msgA.options.sortKey || 0) - (msgB.options.sortKey || 0);
            }).call(_context14, this)); // DATA

            _this3.validationObject.data[validatorName] = validatorOutput.data; // PROMISES

            _forEachInstanceProperty(_context15 = objectUtils.getValuesArray(validatorOutput.promises)).call(_context15, _bindInstanceProperty(_context16 = function _context16(promise) {
              var _this5 = this;

              _newArrowCheck(this, _this4);

              if (promise.isPending && promise.isPending()) {
                activePromisesCount++;

                if (!_this3.attachedPromises.has(promise)) {
                  var _context17;

                  _this3.attachedPromises.add(promise);

                  promise.then(_bindInstanceProperty(_context17 = function _context17(shouldValidate) {
                    _newArrowCheck(this, _this5);

                    _this3.attachedPromises.delete(promise);

                    if (shouldValidate) {
                      _this3.validateInput(true);
                    }
                  }).call(_context17, this));
                }
              }
            }).call(_context16, this));
          }

          _this3.isStandby = !!activePromisesCount;

          if (_this3.isStandby) {
            _this3.validationObject.result = false;
          }
        })();
      }

      this.context.formInstance && this.context.formInstance.validateForm();
      this.updateStatus(); // EMIT VALIDATION RESULT

      this.props.onValidationResult && this.props.onValidationResult({
        result: this.validationObject.result,
        reports: this.validationObject.reports,
        messages: this.validationObject.messages,
        data: this.validationObject.data
      });
    }
    /**
     * @instance
     * @description Updates input status.
     * @returns {void} Void.
     */

  }, {
    key: "updateStatus",
    value: function updateStatus() {
      if (this.isStandby) {
        this.setState({
          status: 'standBy'
        });
      } else if (this.isDeferred) {
        this.setState({
          status: null
        });
      } else if (this.isMissing) {
        this.setState({
          status: (!this.props.deferValidation || this.hasInteracted) && !this.props.keepMissingStatus ? 'invalid' : 'missing'
        });
      } else if (this.validationObject.result === null) {
        this.setState({
          status: this.props.mandatory !== null && !this.isVoid ? 'valid' : null
        });
      } else {
        this.setState({
          status: this.validationObject.result ? 'valid' : 'invalid'
        });
      }
    }
    /**
     * @instance
     * @description Triggers highlight state for the specified amount of time.
     * @param {number} duration - State duration.
     * @returns {void} Void.
     */

  }, {
    key: "highlight",
    value: function highlight(duration) {
      var _this6 = this;

      if (!this.state.overrideStatus) {
        var _context18;

        this.setState({
          overrideStatus: 'highlight'
        });

        _setTimeout(_bindInstanceProperty(_context18 = function _context18() {
          _newArrowCheck(this, _this6);

          if (this.state.overrideStatus === 'highlight') {
            this.setState({
              overrideStatus: false
            });
          }
        }).call(_context18, this), duration || 1000);
      }
    }
    /**
     * @instance
     * @description Triggers warning state for the specified amount of time.
     * @param {number} duration - State duration.
     * @returns {void} Void.
     */

  }, {
    key: "warn",
    value: function warn(duration) {
      var _this7 = this;

      if (!this.state.overrideStatus) {
        var _context19;

        this.setState({
          overrideStatus: 'warning'
        });

        _setTimeout(_bindInstanceProperty(_context19 = function _context19() {
          _newArrowCheck(this, _this7);

          if (this.state.overrideStatus === 'warning') {
            this.setState({
              overrideStatus: false
            });
          }
        }).call(_context19, this), duration || 1000);
      }
    }
    /**
     * @instance
     * @description Clears input.
     * @returns {void} Void.
     */

  }, {
    key: "clear",
    value: function clear() {
      this.hasInteracted = false;
      this.inputChangeTracker = this.props.value;
      this.setNewValue(this.props.value);
    }
    /**
     * @instance
     * @description Changes input value by dispatching a change event.
     * @param {HTMLInputElement} inputElement - Input element.
     * @param {string} value - New value.
     * @returns {void} Void.
     */

  }, {
    key: "emulateChange",
    value: function emulateChange(inputElement, value) {
      var valueSetter = (_Object$getOwnPropertyDescriptor(inputElement, 'value') || {}).set;
      var prototypeValueSetter = (_Object$getOwnPropertyDescriptor(_Object$getPrototypeOf(inputElement), 'value') || {}).set;

      if (valueSetter && valueSetter !== prototypeValueSetter) {
        prototypeValueSetter && prototypeValueSetter.call(inputElement, value);
      } else {
        valueSetter && valueSetter.call(inputElement, value);
      }

      inputElement.dispatchEvent(new Event('change', {
        bubbles: true
      }));
    }
    /**
     * @instance
     * @description Callback for keyDown event.
     * @param {Object<string,*>} e - Triggering event.
     * @returns {void} Void.
     */

  }, {
    key: "onKeyDownHandler",
    value: function onKeyDownHandler(e) {
      // CHAIN PASSED EVENT HANDLER IF NECESSARY
      if (typeof this.props.onKeyDown === 'function') {
        this.props.onKeyDown(e);
      }

      if (e.target.type === 'checkbox' || e.target.type === 'radio') {
        return;
      }

      if (e.target.tagName.toLowerCase() !== 'input' || e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }

      if (e.cancelable) {
        // RUN BLOCKERS
        if (this.inputPluginManager.runBlockers(e)) {
          e.preventDefault();
          this.highlight();
        }
      }
    }
    /**
     * @instance
     * @description Callback for change event.
     * @param {*} e - Triggering event.
     * @returns {void} Void.
     */

  }, {
    key: "onChangeHandler",
    value: function onChangeHandler(e) {
      // CHAIN PASSED EVENT HANDLER IF NECESSARY
      if (typeof this.props.onChange === 'function') {
        this.props.onChange(e);
      }

      var newValue = null;
      var targetType = null;
      var isTargetChecked = null;

      if (objectUtils.isPureObject(e) && e.target) {
        newValue = e.target.value;
        targetType = e.target.type;
        isTargetChecked = e.target.checked;
      } else {
        newValue = e;
      }

      if (typeof newValue !== 'string') {
        if (this.props.stringifier) {
          newValue = this.props.stringifier(newValue);
        } else {
          if (typeof newValue !== 'undefined' && newValue !== null) {
            if (Object.hasOwnProperty.call(newValue, 'toString') || Object.hasOwnProperty.call(_Object$getPrototypeOf(newValue), 'toString')) {
              newValue = newValue.toString();
            }
          }
        }
      }

      if (typeof newValue !== 'string') {
        newValue = '';
      } // INDICATE THAT THERE HAS BEEN INTERACTION


      this.hasInteracted = true;

      if (typeof this.props.booleanInput === 'boolean' ? this.props.booleanInput : targetType === 'checkbox') {
        this.setNewValue(isTargetChecked ? 'true' : 'false');
      } else {
        this.setNewValue(newValue);
      }
    }
    /**
     * @instance
     * @description Callback for blur event.
     * @param {Object<string,*>} e - Triggering event.
     * @returns {void} Void.
     */

  }, {
    key: "onBlurHandler",
    value: function onBlurHandler(e) {
      // CHAIN PASSED EVENT HANDLER IF NECESSARY
      if (typeof this.props.onBlur === 'function') {
        this.props.onBlur(e);
      } // INDICATE THAT THERE HAS BEEN INTERACTION


      if (!this.hasInteracted) {
        this.hasInteracted = true;

        if (this.props.deferValidation) {
          this.validateInput();
        }
      } // IMITATE onChange EVENT REAL SPECIFICATION BEHAVIOUR


      if (e.target.value !== this.inputChangeTracker) {
        // RESET inputChangeTracker
        this.inputChangeTracker = e.target.value;

        if (e.target.type === 'checkbox' || e.target.type === 'radio') {
          return;
        } // RUN FILTERS


        var newValue = this.inputPluginManager.runFilters(e.target.value);

        if (newValue !== e.target.value) {
          // UPDATE inputChangeTracker
          this.inputChangeTracker = newValue; // EMULATE INPUT CHANGE

          this.emulateChange(e.target, newValue);
          this.highlight();
        }
      }
    }
    /**
     * @instance
     * @description React render method.
     * @returns {Object<string,*>} React component.
     */

  }, {
    key: "render",
    value: function render() {
      var _this$props = this.props,
          name = _this$props.name,
          parser = _this$props.parser;
      var _this$state = this.state,
          value = _this$state.value,
          status = _this$state.status,
          overrideStatus = _this$state.overrideStatus;
      var id = this.id,
          hasInteracted = this.hasInteracted,
          validationObject = this.validationObject,
          setNewValue = this.setNewValue,
          onKeyDownHandler = this.onKeyDownHandler,
          onChangeHandler = this.onChangeHandler,
          onBlurHandler = this.onBlurHandler;
      return this.props.inputRenderFn ? this.props.inputRenderFn({
        id: id,
        name: name,
        value: parser ? parser(value) : value,
        onKeyDown: onKeyDownHandler,
        onChange: onChangeHandler,
        onBlur: onBlurHandler
      }, {
        status: overrideStatus || status,
        hasInteracted: hasInteracted,
        validationObject: validationObject,
        setNewValue: setNewValue
      }) : null;
    }
  }]);

  return InputRenderer;
}(React.Component);

_defineProperty(InputRenderer, "contextType", InputContext);

_defineProperty(InputRenderer, "defaultProps", {
  voidValue: '',
  booleanInput: null,
  mandatory: true,
  keepMissingStatus: false,
  runFiltersBeforeValidators: false,
  match: null,
  errorMessages: {
    mandatory: null,
    match: null
  },
  plugins: [],
  stringifier: null,
  parser: null,
  deferValidation: true,
  onValidationResult: null,
  onKeyDown: null,
  onChange: null,
  onBlur: null
});

export { InputContext, InputRenderer };